import { Outlet, RouteObject } from 'react-router-dom';
import { WorkspaceErrorElement } from '../components/errors/RouteErrorElement';
import { ProtectedRoute } from '../navigation/ProtectedRoute';
import { ForgeLayout } from './components/layout/ForgeLayout';
import {
  FORGE_CLEANING_TOOL_PATH,
  FORGE_DISTRACTORS_PATH,
  FORGE_ITEM_SEARCH_PATH,
  FORGE_PR_REVIEW_PATH,
  FORGE_RECOGNITION_OKRS_PATH,
  FORGE_REMOTE_DC_TASK_LIST_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
  FORGE_SYSTEM_HEALTH_PATH,
} from './paths';
import { forgeRoutes } from './routes';
import { loadRouteChunk } from './utils/lazyRouteModule';

const forgeErrorElement = (
  <WorkspaceErrorElement homePath={FORGE_SYSTEM_HEALTH_PATH} homeLabel="Back to system health" />
);

// The Forge shell. The labeler tools and the pages with their own capability sit here; every
// other Forge page is in `forgeRoutes`, behind the internal-access gate.
export const forgeShellRoutes: RouteObject = {
  element: <ForgeLayout />,
  children: [
    {
      errorElement: forgeErrorElement,
      children: [
        {
          path: FORGE_CLEANING_TOOL_PATH,
          children: [
            {
              index: true,
              handle: { pageWidth: 'full' },
              lazy: async () => ({
                Component: (await loadRouteChunk(() => import('./pages/admin/CleaningToolPage')))
                  .CleaningToolPage,
              }),
            },
          ],
        },
        {
          path: FORGE_ITEM_SEARCH_PATH,
          children: [
            {
              index: true,
              handle: { pageWidth: 'capped' },
              lazy: async () => ({
                Component: (await loadRouteChunk(() => import('./pages/admin/ItemSearchPage')))
                  .ItemSearchPage,
              }),
            },
          ],
        },
        {
          path: FORGE_REMOTE_DC_TOOL_PATH,
          element: (
            <ProtectedRoute capability="hasRemoteDataCollectionAccess" element={<Outlet />} />
          ),
          children: [
            {
              index: true,
              handle: { pageWidth: 'full' },
              lazy: async () => ({
                Component: (
                  await loadRouteChunk(() => import('./pages/admin/MasterDataCollectionPage'))
                ).MasterDataCollectionPage,
              }),
            },
          ],
        },
        {
          path: FORGE_DISTRACTORS_PATH,
          element: (
            <ProtectedRoute capability="hasRemoteDataCollectionAccess" element={<Outlet />} />
          ),
          children: [
            {
              index: true,
              handle: { pageWidth: 'full' },
              lazy: async () => ({
                Component: (await loadRouteChunk(() => import('./pages/admin/DistractorsPage')))
                  .DistractorsPage,
              }),
            },
          ],
        },
        {
          path: FORGE_REMOTE_DC_TASK_LIST_PATH,
          element: (
            <ProtectedRoute capability="hasRemoteDataCollectionAccess" element={<Outlet />} />
          ),
          children: [
            {
              index: true,
              handle: { pageWidth: 'full' },
              lazy: async () => ({
                Component: (
                  await loadRouteChunk(
                    () => import('./pages/dashboard/RemoteDataCollectionDashboard')
                  )
                ).RemoteDataCollectionDashboard,
              }),
            },
          ],
        },
        {
          path: FORGE_RECOGNITION_OKRS_PATH,
          children: [
            {
              index: true,
              handle: { pageWidth: 'capped' },
              lazy: async () => ({
                Component: (
                  await loadRouteChunk(() => import('./pages/dashboard/RecognitionOkrsDashboard'))
                ).RecognitionOkrsDashboard,
              }),
            },
          ],
        },
        {
          path: FORGE_PR_REVIEW_PATH,
          children: [
            {
              index: true,
              handle: { pageWidth: 'full' },
              lazy: async () => ({
                Component: (
                  await loadRouteChunk(() => import('./pages/admin/RecognitionReviewToolPage'))
                ).RecognitionReviewToolPage,
              }),
            },
            {
              path: 'sets',
              handle: { pageWidth: 'capped' },
              lazy: async () => ({
                Component: (
                  await loadRouteChunk(() => import('./pages/admin/RecognitionReviewSetsPage'))
                ).RecognitionReviewSetsPage,
              }),
            },
          ],
        },
        {
          ...forgeRoutes,
          element: <ProtectedRoute element={<Outlet />} />,
        },
      ],
    },
  ],
};
